import { BACKEND_API_URL, getClerkToken, authenticatedFetch } from "./apiClient";
import { normalizeBrokerApiPath } from "./brokerIntegrationApi";
import { handleRateLimitedResponse } from "../utils/httpRateLimit";
import { parseApiErrorBody } from "../utils/parseApiError";
import type { ParsedApiError } from "../utils/parseApiError";
import { ApiRequestError } from "../utils/apiRequestError";
import {
  normalizeCsvImportTemplate,
  resolveCustomCsvTemplatesEndpoint,
} from "../utils/customCsvTemplates";
import type {
  CsvImportProfile,
  CsvImportProfilePayload,
  CustomCsvFormFieldsInput,
  CustomCsvImportResponse,
  CustomCsvInspectResponse,
  CustomCsvPreviewResponse,
} from "../types/customCsvImport";
import type { CustomCsvImportCapabilities } from "./brokerIntegrationApi";

const DEFAULT_PREVIEW_ENDPOINT = "/broker/custom-csv/preview";
const DEFAULT_IMPORT_ENDPOINT = "/broker/custom-csv/import";
const DEFAULT_INSPECT_ENDPOINT = "/broker/custom-csv/inspect";

function resolveCustomCsvEndpoint(path: string): string {
  return normalizeBrokerApiPath(path);
}

export function buildCustomCsvFormData(
  input: CustomCsvFormFieldsInput,
  options?: { forImport?: boolean }
): FormData {
  const form = new FormData();
  form.append("file", input.file);
  form.append("time_zone", input.timeZone.trim());
  form.append("import_mode", input.importMode);
  form.append("column_mapping", JSON.stringify(input.columnMapping));
  form.append("value_transforms", JSON.stringify(input.valueTransforms ?? {}));

  const defaults = { ...(input.defaults ?? {}) };
  if (input.importInstrument?.trim()) {
    defaults.import_instrument = input.importInstrument.trim();
  }
  form.append("defaults", JSON.stringify(defaults));

  if (input.tradedAssetClasses?.length) {
    form.append("traded_asset_classes", input.tradedAssetClasses.join(","));
  }
  // Omit or empty → BE stores []; per-symbol class is confirmed in instrument setup.

  const templateSlug = input.templateSlug?.trim() || input.profileSlug?.trim();
  if (input.layoutShapeId?.trim() && !templateSlug) {
    form.append("layout_shape_id", input.layoutShapeId.trim());
  }
  if (templateSlug) {
    form.append("template_slug", templateSlug);
    form.append("profile_slug", templateSlug);
  }
  if (input.accountName?.trim()) {
    form.append("account_name", input.accountName.trim());
  }
  if (input.importInstrument?.trim() && !input.columnMapping.symbol?.trim()) {
    form.append("import_instrument", input.importInstrument.trim());
  }
  if (input.allowPartial) {
    form.append("allow_partial", "true");
  }
  if (input.headerRowIndex != null && Number.isFinite(input.headerRowIndex)) {
    form.append("header_row_index", String(Math.max(0, Math.floor(input.headerRowIndex))));
  }

  if (options?.forImport) {
    if (!input.previewToken?.trim()) {
      throw new Error("preview_token is required for import");
    }
    form.append("preview_token", input.previewToken.trim());
    form.append("broker_name", "custom_csv");
    if (!input.displayName?.trim()) {
      throw new Error("display_name is required for import");
    }
    if (!input.accountName?.trim()) {
      throw new Error("account_name is required for import");
    }
    form.append("display_name", input.displayName.trim());
    form.append("account_name", input.accountName.trim());
    if (input.acknowledgedWarningCodes?.length) {
      form.append("acknowledged_warning_codes", input.acknowledgedWarningCodes.join(","));
    }
    const saveTemplate = input.saveTemplate ?? input.saveProfile;
    if (saveTemplate) {
      form.append("save_template", "true");
      form.append("save_profile", "true");
    }
  }

  return form;
}

export type CustomCsvErrorDetail =
  | string
  | {
      errors?: Array<{
        row: number | null;
        field: string | null;
        message: string;
        error_code?: string;
        column?: string;
        raw_value?: string;
        expected?: string;
      }>;
      warnings?: unknown[];
    };

export async function readCustomCsvErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  const text = await response.text().catch(() => "");
  return parseCustomCsvErrorMessageFromBody(text, fallback);
}

function parseCustomCsvErrorMessageFromBody(text: string, fallback: string): string {
  if (!text.trim()) return fallback;
  try {
    const body = JSON.parse(text) as Record<string, unknown>;
    return buildCustomCsvParsedError(body, fallback).message;
  } catch {
    if (text.trim().length < 500) return text.trim();
  }
  return fallback;
}

function buildCustomCsvParsedError(body: Record<string, unknown>, fallback: string): ParsedApiError {
  const parsed = parseApiErrorBody(body, fallback);
  if (parsed.slug !== "http_error") {
    return parsed;
  }

  const nestedErrors = parsed.details?.errors;
  if (Array.isArray(nestedErrors) && nestedErrors.length) {
    const first = nestedErrors[0] as { row?: number; field?: string; message?: string };
    const row = first.row != null ? `Row ${first.row}: ` : "";
    const field = first.field ? `${first.field} — ` : "";
    return {
      slug: "csv_import.parse_failed",
      message: `${row}${field}${first.message ?? ""}`.trim() || fallback,
      details: parsed.details,
      rawBody: body,
    };
  }

  const legacyDetail = body.detail;
  if (typeof legacyDetail === "string" && legacyDetail.trim()) {
    return { slug: "csv_import.parse_failed", message: legacyDetail.trim(), rawBody: body };
  }
  if (legacyDetail && typeof legacyDetail === "object" && !Array.isArray(legacyDetail)) {
    const legacyErrors = (legacyDetail as { errors?: Array<{ row?: number; field?: string; message?: string }> }).errors;
    if (Array.isArray(legacyErrors) && legacyErrors.length) {
      const first = legacyErrors[0];
      const row = first.row != null ? `Row ${first.row}: ` : "";
      const field = first.field ? `${first.field} — ` : "";
      return {
        slug: "csv_import.parse_failed",
        message: `${row}${field}${first.message ?? ""}`.trim() || fallback,
        details: { errors: legacyErrors },
        rawBody: body,
      };
    }
  }

  if (parsed.message.trim() && parsed.message !== fallback) {
    return { ...parsed, slug: "csv_import.parse_failed" };
  }

  return parsed;
}

async function throwCustomCsvNotOk(response: Response, fallback: string): Promise<never> {
  const text = await response.text().catch(() => "");
  let body: Record<string, unknown> = {};
  try {
    body = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    body = {};
  }

  if (response.status === 401) {
    throw new ApiRequestError("Session expired. Please log in again.", 401, {
      slug: "auth.token_expired",
      message: "Session expired. Please log in again.",
    });
  }

  const parsed = buildCustomCsvParsedError(body, fallback);
  const message = parsed.message?.trim() || fallback;
  throw new ApiRequestError(message, response.status, parsed);
}

function throwIfNoAuthToken(): never {
  throw new ApiRequestError("Authentication required. Please sign in.", 401, {
    slug: "auth.required",
    message: "Authentication required. Please sign in.",
  });
}

async function postCustomCsvMultipart<T>(
  endpoint: string,
  formData: FormData
): Promise<T> {
  const path = resolveCustomCsvEndpoint(endpoint);
  const isDemoPublic = path.includes("/demo/");
  const headers: Record<string, string> = {};
  if (!isDemoPublic) {
    const token = await getClerkToken();
    if (!token) {
      throwIfNoAuthToken();
    }
    headers.Authorization = `Bearer ${token}`;
  }

  const url = `${BACKEND_API_URL}${path}`;
  const response = await fetch(url, {
    method: "POST",
    headers,
    credentials: isDemoPublic ? "include" : "same-origin",
    body: formData,
  });

  if (handleRateLimitedResponse(response, path)) {
    throw new ApiRequestError("Too many requests. Please try again later.", 429, {
      slug: "rate_limit.exceeded",
      message: "Too many requests. Please try again later.",
    });
  }

  if (!response.ok) {
    await throwCustomCsvNotOk(response, `Request failed (${response.status})`);
  }

  return response.json() as Promise<T>;
}

export async function postCustomCsvPreview(
  formData: FormData,
  endpoint = DEFAULT_PREVIEW_ENDPOINT
): Promise<CustomCsvPreviewResponse> {
  return postCustomCsvMultipart<CustomCsvPreviewResponse>(endpoint, formData);
}

export async function postCustomCsvInspect(
  file: File,
  options?: { maxPreviewRows?: number; endpoint?: string }
): Promise<CustomCsvInspectResponse> {
  const form = new FormData();
  form.append("file", file);
  const maxRows = options?.maxPreviewRows;
  if (maxRows != null && Number.isFinite(maxRows)) {
    form.append("max_preview_rows", String(Math.min(30, Math.max(1, Math.floor(maxRows)))));
  }
  return postCustomCsvMultipart<CustomCsvInspectResponse>(
    options?.endpoint?.trim() || DEFAULT_INSPECT_ENDPOINT,
    form
  );
}

export async function postCustomCsvImport(
  formData: FormData,
  endpoint = DEFAULT_IMPORT_ENDPOINT
): Promise<CustomCsvImportResponse> {
  return postCustomCsvMultipart<CustomCsvImportResponse>(endpoint, formData);
}

export async function fetchCustomCsvTemplates(
  endpointOrCapability?: string | Pick<CustomCsvImportCapabilities, "templates_endpoint" | "profiles_endpoint"> | null
): Promise<{ templates: CsvImportProfile[] }> {
  const endpoint =
    typeof endpointOrCapability === "string"
      ? endpointOrCapability
      : resolveCustomCsvTemplatesEndpoint(endpointOrCapability ?? null);
  const path = resolveCustomCsvEndpoint(endpoint);
  const response = await authenticatedFetch(path);
  if (!response.ok) {
    await throwCustomCsvNotOk(response, `Failed to load templates (${response.status})`);
  }
  const data = (await response.json()) as {
    templates?: Record<string, unknown>[];
    profiles?: Record<string, unknown>[];
    template?: Record<string, unknown>;
    profile?: Record<string, unknown>;
  };
  const raw = data.templates ?? data.profiles ?? [];
  return { templates: raw.map((row) => normalizeCsvImportTemplate(row)) };
}

/** @deprecated Use fetchCustomCsvTemplates */
export const fetchCustomCsvProfiles = fetchCustomCsvTemplates;

export async function saveCustomCsvTemplate(
  payload: CsvImportProfilePayload,
  endpointOrCapability?: string | Pick<CustomCsvImportCapabilities, "templates_endpoint" | "profiles_endpoint"> | null
): Promise<CsvImportProfile> {
  const endpoint =
    typeof endpointOrCapability === "string"
      ? endpointOrCapability
      : resolveCustomCsvTemplatesEndpoint(endpointOrCapability ?? null);
  const path = resolveCustomCsvEndpoint(endpoint);
  const response = await authenticatedFetch(path, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    await throwCustomCsvNotOk(response, `Failed to save template (${response.status})`);
  }
  const data = (await response.json()) as {
    template?: Record<string, unknown>;
    profile?: Record<string, unknown>;
  };
  const row = data.template ?? data.profile;
  if (row && typeof row === "object") {
    return normalizeCsvImportTemplate(row);
  }
  return normalizeCsvImportTemplate({ ...payload, profile_slug: "" });
}

/** @deprecated Use saveCustomCsvTemplate */
export const saveCustomCsvProfile = saveCustomCsvTemplate;
